import { useEffect, useMemo, useState } from "react";
import type { CheatsheetCtx, Command } from "../cheatsheets/types";
import { resolveCheatsheet } from "../cheatsheets/content";
import { fillCommand } from "../cheatsheets/fillCommand";

interface Props {
  ctx: CheatsheetCtx | null;
  onClose: () => void;
}

/**
 * Non-modal side drawer (~420px wide, right-docked) that renders a
 * context-aware troubleshooting cheatsheet. The rest of the dashboard
 * stays visible — this is by design, so you can correlate commands to
 * what's on screen.
 */
export function CheatsheetDrawer({ ctx, onClose }: Props) {
  useEffect(() => {
    if (!ctx) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [ctx, onClose]);

  const sections = useMemo(() => (ctx ? resolveCheatsheet(ctx) : []), [ctx]);

  if (!ctx) return null;

  return (
    <div
      className="drawer"
      role="complementary"
      aria-label="Troubleshooting cheatsheet"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: 420,
        background: "var(--bg-1)",
        borderLeft: "1px solid var(--line-2)",
        zIndex: 90,
        display: "flex",
        flexDirection: "column",
        boxShadow: "var(--shadow-2)",
      }}
    >
      <header
        style={{
          padding: "12px 14px 10px 16px",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          background: "rgba(78,225,240,0.04)",
        }}
      >
        <div
          style={{
            width: 3,
            alignSelf: "stretch",
            background: "var(--cyan)",
            borderRadius: 2,
            boxShadow: "0 0 8px var(--cyan)",
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 9,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--cyan)",
              marginBottom: 3,
              fontFamily: "var(--mono)",
            }}
          >
            Cheatsheet
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--fg-0)",
              lineHeight: 1.25,
            }}
          >
            {ctx.title}
          </div>
          {ctx.subtitle && (
            <div
              style={{
                fontSize: 11,
                color: "var(--fg-2)",
                fontFamily: "var(--mono)",
                marginTop: 3,
              }}
            >
              {ctx.subtitle}
            </div>
          )}
        </div>
        <button
          className="btn xs ghost"
          onClick={onClose}
          title="close (Esc)"
          aria-label="Close cheatsheet"
        >
          esc ✕
        </button>
      </header>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "8px 0 16px",
        }}
      >
        {sections.length === 0 && (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--fg-3)",
              fontSize: 12,
            }}
          >
            No cheatsheet registered for this card yet.
          </div>
        )}
        {sections.map((s) => (
          <Section key={s.label} label={s.label}>
            {s.cmds.map((c, i) => (
              <CommandLine key={i} cmd={c} ctx={ctx} />
            ))}
          </Section>
        ))}
      </div>

      <footer
        style={{
          borderTop: "1px solid var(--line)",
          padding: "8px 14px",
          fontSize: 10,
          color: "var(--fg-3)",
          fontFamily: "var(--mono)",
          display: "flex",
          gap: 14,
          alignItems: "center",
        }}
      >
        <span>
          <span className="kbd">?</span> open · <span className="kbd">Esc</span> close
        </span>
        <span style={{ flex: 1 }} />
        <span>click cmd to copy</span>
      </footer>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 10 }}>
      <div
        style={{
          padding: "6px 16px",
          fontSize: 9,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--fg-2)",
          fontFamily: "var(--mono)",
          fontWeight: 600,
          background: "var(--bg-2)",
          borderTop: "1px solid var(--line)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        {label}
      </div>
      <div style={{ padding: "6px 12px" }}>{children}</div>
    </section>
  );
}

function CommandLine({ cmd, ctx }: { cmd: Command; ctx: CheatsheetCtx }) {
  const filled = fillCommand(cmd.cmd, ctx);
  const isComment = filled.startsWith("#");
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (isComment) return;
    try {
      await navigator.clipboard.writeText(filled);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — fail silently */
    }
  }

  if (isComment) {
    return (
      <div
        style={{
          padding: "4px 10px",
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--fg-3)",
          whiteSpace: "pre-wrap",
          lineHeight: 1.55,
        }}
      >
        {filled}
      </div>
    );
  }

  const danger = cmd.danger;
  const borderColor = danger ? "rgba(255,95,122,0.35)" : "var(--line)";
  const stripeColor = danger ? "var(--rose)" : "var(--cyan)";

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "stretch",
        marginTop: 6,
        background: danger ? "rgba(255,95,122,0.04)" : "var(--bg-2)",
        border: `1px solid ${borderColor}`,
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: 2,
          background: stripeColor,
          flexShrink: 0,
        }}
      />
      <button
        type="button"
        onClick={copy}
        title={copied ? "copied!" : "click to copy"}
        style={{
          flex: 1,
          padding: "7px 28px 7px 10px",
          textAlign: "left",
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontFamily: "var(--mono)",
            fontSize: 11.5,
            color: danger ? "var(--rose)" : "var(--fg-0)",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            lineHeight: 1.55,
          }}
        >
          {filled}
        </div>
        {cmd.note && (
          <div
            style={{
              marginTop: 3,
              fontSize: 10,
              color: "var(--fg-2)",
              fontFamily: "var(--mono)",
            }}
          >
            {cmd.note}
          </div>
        )}
        {danger && (
          <div
            style={{
              marginTop: 4,
              fontSize: 9,
              color: "var(--rose)",
              fontFamily: "var(--mono)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            ⚠ destructive — double-check context
          </div>
        )}
      </button>
      <div
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          fontSize: 9,
          color: copied ? "var(--emerald)" : "var(--fg-3)",
          fontFamily: "var(--mono)",
          letterSpacing: "0.14em",
          pointerEvents: "none",
          transition: "color 200ms",
        }}
      >
        {copied ? "✓ COPIED" : "COPY"}
      </div>
    </div>
  );
}
